/** Run sheet / timeline — four time slots with descriptions and side image. */
import { cn } from '@rocco/utils/cn';
import { useInView } from 'react-intersection-observer';
import { ScrollReveal } from './components/ScrollReveal';
import { HDEDesktopFrame } from './components/HDEDesktopFrame';
import { HDEPresentedByMark } from './components/HDEPresentedByMark';

interface TimeSlotProps {
    prefix?: string;
    startTime: string;
    endTime?: string;
    startPeriod?: string;
    endPeriod?: string;
    sharedPeriod?: string;
    title: string;
    descriptions: { text?: string; html?: string; muted?: boolean }[];
    isVisible?: boolean;
    delay?: number;
}

export interface RunSheetTimeSlot {
    prefix?: string;
    startTime: string;
    endTime?: string;
    startPeriod?: string;
    endPeriod?: string;
    sharedPeriod?: string;
    title: string;
    descriptions: { text?: string; html?: string; muted?: boolean }[];
}

const TimeSlot = ({
    prefix,
    startTime,
    endTime,
    startPeriod,
    endPeriod,
    sharedPeriod,
    title,
    descriptions,
    isVisible = false,
    delay = 0,
}: TimeSlotProps) => (
    <>
        <div className="overflow-hidden">
            <div
                className={cn(
                    'h-px w-full origin-left bg-black/20',
                    isVisible && 'hde-line-draw',
                )}
                style={{ animationDelay: `${delay}ms` }}
            />
        </div>
        <ScrollReveal animation="fade-up" duration={500} delay={delay}>
            <div className="flex items-start pt-4.75 pb-[6.2rem]">
                {/* Left: time + title */}
                <div className="flex w-[18.625rem] shrink-0 flex-col gap-[0.25rem]">
                    <div className="flex items-center gap-0.5">
                        <span className="font-display text-[1.93rem] leading-[0.9] font-[450] tracking-[-0.058rem] text-black uppercase opacity-40">
                            {prefix ? `${prefix} ${startTime}` : startTime}
                        </span>
                        {startPeriod && !sharedPeriod ? (
                            <span className="mt-0.75 self-start font-display text-[1.034rem] leading-[0.9] font-[450] tracking-[-0.031rem] text-black uppercase opacity-40">
                                {startPeriod}
                            </span>
                        ) : null}
                        {endTime ? (
                            <span className="font-display text-[1.93rem] leading-[0.9] font-[450] tracking-[-0.058rem] text-black uppercase opacity-40">
                                - {endTime}
                            </span>
                        ) : null}
                        {endPeriod ? (
                            <span className="mt-0.75 self-start font-display text-[1.034rem] leading-[0.9] font-[450] tracking-[-0.031rem] text-black uppercase opacity-40">
                                {endPeriod}
                            </span>
                        ) : null}
                        {sharedPeriod ? (
                            <span className="mt-0.75 self-start font-display text-[1.034rem] leading-[0.9] font-[450] tracking-[-0.031rem] text-black uppercase opacity-40">
                                {sharedPeriod}
                            </span>
                        ) : null}
                    </div>
                    <p className="font-display text-[1.93rem] leading-[0.9] font-bold tracking-[-0.058rem] whitespace-nowrap text-black uppercase">
                        {title}
                    </p>
                </div>

                {/* Spacer */}
                <div className="w-[27.75rem] shrink-0" />

                {/* Right: description */}
                <div className="flex w-137.75 flex-col gap-[1.25rem] pr-[1.875rem]">
                    {descriptions.map((desc, i) => (
                        <div key={i}>
                            {desc.html ? (
                                <div
                                    className="font-sans text-[1.125rem] leading-tight font-medium tracking-[-0.0225rem] text-[#818585] [&_p]:mb-[1.25rem] [&_p:last-child]:mb-0 [&_strong]:font-medium [&_strong]:text-black"
                                    dangerouslySetInnerHTML={{
                                        __html: desc.html.replace(
                                            /<p>(\s|&nbsp;|<br\s*\/?>)*<\/p>/gi,
                                            '',
                                        ),
                                    }}
                                />
                            ) : (
                                <p
                                    className={`font-sans text-[1.125rem] leading-tight font-medium tracking-[-0.0225rem] ${
                                        desc.muted
                                            ? 'text-[#818585]'
                                            : 'text-black'
                                    }`}
                                >
                                    {desc.text}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </ScrollReveal>
    </>
);

const defaultTimeSlots: TimeSlotProps[] = [
    {
        prefix: 'From ',
        startTime: '5:00',
        sharedPeriod: 'pm',
        title: 'Explore & Connect',
        descriptions: [
            {
                text: 'Walk the floor. Meet 40+ exhibiting brands and professionals. Scan QR codes to save everything to your ArchiPro account - products, specs and contacts - all in one place.',
            },
            {
                text: 'Browse exhibitors from kitchen and bathroom to flooring, lighting, outdoor living and more. Every exhibitor is an ArchiPro-vetted business.',
                muted: true,
            },
        ],
    },
    {
        startTime: '6:00',
        endTime: '6:45',
        sharedPeriod: 'pm',
        title: 'the Professional panel',
        descriptions: [
            {
                text: 'Join us for a session strictly for the professional community, featuring two leading Australian practices challenging the way the industry operates - from how buildings get made, to how studios get run. A conversation about what a better model looks like and what NZ practices can take from it.',
            },
        ],
    },
    {
        startTime: '7:00',
        endTime: '7:30',
        sharedPeriod: 'pm',
        title: 'THE PUBLIC PANEL',
        descriptions: [
            {
                text: "Hosted by Tom Webster - architect and host of Grand Designs New Zealand. Tom sits down with six of Australasia's most accomplished architects, designers and builders for the conversation most homeowners never get to hear: the decisions made before construction starts that determine whether a home works, what it costs and how it feels to live in.",
            },
        ],
    },
    {
        startTime: '7:30',
        endTime: '9:00',
        sharedPeriod: 'pm',
        title: 'Connect & Continue',
        descriptions: [
            {
                text: "The conversations continue. Return to exhibitors, connect with panellists and use your ArchiPro App to enquire with professionals you've met.",
            },
            {
                text: 'Drinks and canapés provided. This is the part where plans start to take shape.',
                muted: true,
            },
        ],
    },
];

interface RunSheetProps {
    /** Pre-composed poster from the CMS (EventDetails.RunSheetPoster); rendered as-is in the card frame. */
    posterImage?: string | null;
    onRegister?: () => void;
    showRegisterButton?: boolean;
    timeSlots?: RunSheetTimeSlot[];
    isMobile?: boolean;
    /** When false, hides “Presented by” and sponsor mark (e.g. no active register-module event). */
    showPresentedBySponsor?: boolean;
    presentedByLogoUrl?: string | null;
    presentedByProfilePath?: string | null;
    presentedBySponsorName?: string | null;
}

export const RunSheet = ({
    posterImage,
    onRegister,
    showRegisterButton = true,
    timeSlots,
    isMobile = false,
    showPresentedBySponsor = false,
    presentedByLogoUrl,
    presentedByProfilePath,
    presentedBySponsorName,
}: RunSheetProps) => {
    const { ref: sectionRef, inView } = useInView({
        triggerOnce: true,
        threshold: 0.15,
    });

    const hasPresentedBy =
        showPresentedBySponsor && Boolean(presentedByLogoUrl?.trim());

    if (timeSlots && timeSlots.length === 0) {
        return null;
    }

    const slotsToRender: TimeSlotProps[] =
        timeSlots && timeSlots.length > 0
            ? timeSlots.map((slot) => ({
                  prefix: slot.prefix,
                  startTime: slot.startTime,
                  endTime: slot.endTime,
                  startPeriod: slot.startPeriod,
                  endPeriod: slot.endPeriod,
                  sharedPeriod: slot.sharedPeriod,
                  title: slot.title,
                  descriptions: slot.descriptions,
              }))
            : defaultTimeSlots;

    if (isMobile) {
        return (
            <section
                ref={sectionRef}
                data-testid="run-sheet"
                className="relative w-full bg-white px-[1.5rem] pt-[1.5rem]"
            >
                {hasPresentedBy ? (
                    <div className="flex items-center gap-[0.75rem] pb-8">
                        <p className="font-sans text-[0.625rem] font-medium tracking-[0.125em] text-[#515151] uppercase">
                            Presented by
                        </p>
                        <HDEPresentedByMark
                            logoUrl={presentedByLogoUrl}
                            profilePath={presentedByProfilePath}
                            sponsorName={presentedBySponsorName}
                            className="mt-1"
                            imageClassName="max-h-[2rem] w-[9.25rem]"
                        />
                    </div>
                ) : null}
                <div>
                    {slotsToRender.map((slot, index) => (
                        <div
                            key={`${slot.title}-${index}`}
                            className="border-t border-[#dbdbdb] py-6.25"
                        >
                            <p className="font-display text-[1.125rem] leading-none tracking-[-0.03em] text-[#828585] uppercase">
                                {slot.prefix ? `${slot.prefix} ` : ''}
                                {slot.startTime}
                                {slot.endTime ? ` - ${slot.endTime}` : ''}
                                {slot.sharedPeriod
                                    ? ` ${slot.sharedPeriod}`
                                    : ''}
                            </p>
                            <p className="mt-1.25 font-display text-[1.125rem] leading-none font-bold tracking-[-0.02em] text-black uppercase">
                                {slot.title}
                            </p>
                            {slot.descriptions.map((desc, descIndex) => (
                                <div key={`${slot.title}-${descIndex}`}>
                                    {desc.html ? (
                                        <div
                                            className={`mt-[0.25rem] text-[0.75rem] leading-[1.42] font-normal [&_strong]:font-normal ${
                                                desc.muted
                                                    ? 'text-[#818585]'
                                                    : 'text-[#141414]'
                                            }`}
                                            dangerouslySetInnerHTML={{
                                                __html: desc.html,
                                            }}
                                        />
                                    ) : (
                                        <p
                                            className={`mt-[0.25rem] text-[0.75rem] leading-[1.42] font-normal ${
                                                desc.muted
                                                    ? 'text-[#818585]'
                                                    : 'text-[#141414]'
                                            }`}
                                        >
                                            {desc.text}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
                <div
                    className="relative aspect-[25.1875/44.8125] w-full shrink-0 overflow-hidden rounded-[0.375rem]"
                    style={{ backgroundColor: '#2a1c14' }}
                >
                    {posterImage ? (
                        <img
                            src={posterImage}
                            alt="Home Design Evening"
                            className="absolute inset-0 h-full w-full object-cover"
                        />
                    ) : null}
                </div>
                <button
                    type="button"
                    onClick={onRegister}
                    className={`mt-[1.5rem] mb-[2.25rem] flex h-[3.25rem] w-full items-center justify-center rounded-full bg-[#141414] ${showRegisterButton ? '' : 'hidden'}`}
                >
                    <span className="text-[0.9375rem] text-white">
                        Register Now — Free
                    </span>
                </button>
            </section>
        );
    }

    return (
        <section
            ref={sectionRef}
            data-testid="run-sheet"
            className="relative w-full bg-white pt-[9.65rem]"
        >
            <HDEDesktopFrame>
                {hasPresentedBy ? (
                    <div className="flex items-start gap-[0.75rem] px-[3.125rem] pt-[1.15rem] pb-[2.5rem]">
                        <p className="mt-[1.8rem] font-sans text-[0.8125rem] font-medium tracking-[0.065em] text-[#515151] uppercase">
                            Presented by
                        </p>
                        <HDEPresentedByMark
                            logoUrl={presentedByLogoUrl}
                            profilePath={presentedByProfilePath}
                            sponsorName={presentedBySponsorName}
                            className="mt-3"
                            imageClassName="max-h-[2.75rem] w-45.75"
                        />
                    </div>
                ) : null}
                <div className="flex items-start gap-12 px-[3.125rem]">
                    {/* Timeline column */}
                    <div className="flex w-[85rem] shrink-0 flex-col">
                        {slotsToRender.map((slot, i) => (
                            <TimeSlot
                                key={i}
                                {...slot}
                                isVisible={inView}
                                delay={i * 150}
                            />
                        ))}

                        {/* Register pill */}
                        <ScrollReveal
                            animation="fade-up"
                            duration={400}
                            delay={600}
                        >
                            <button
                                type="button"
                                onClick={onRegister}
                                className={`flex h-[3.125rem] items-center justify-center self-start rounded-full border border-black bg-[#1e1e1e] px-[2.234rem] transition-colors hover:bg-gray-700 active:scale-[0.97] ${showRegisterButton ? '' : 'hidden'}`}
                            >
                                <span className="text-[1.058rem] leading-[1.713rem] font-normal whitespace-nowrap text-white">
                                    Register Now – Free
                                </span>
                            </button>
                        </ScrollReveal>
                    </div>

                    {/* Side image card */}
                    <ScrollReveal
                        animation="fade-right"
                        duration={700}
                        delay={300}
                    >
                        <div
                            className="relative shrink-0 overflow-hidden"
                            style={{
                                width: '25.1875rem',
                                height: '44.8125rem',
                                backgroundColor: '#2a1c14',
                            }}
                        >
                            {posterImage ? (
                                <img
                                    src={posterImage}
                                    alt="Home Design Evening"
                                    className="absolute inset-0 h-full w-full rounded-[0.375rem] object-cover"
                                />
                            ) : null}
                        </div>
                    </ScrollReveal>
                </div>
            </HDEDesktopFrame>
            <div className="mt-40 md:h-px md:bg-[#d7d7d7]"></div>
        </section>
    );
};
